import type { components } from '@/api/generated/schema';
import { archiveSections } from './sections.gen';

export type ArchiveRow = components['schemas']['HistoricalWorkflowHistory'];
type Slot = components['schemas']['ArchiveText'] | components['schemas']['ArchiveJSON'];
function SourceValue({slot}:{slot?:Slot}) {
 if (!slot || slot.presence==='absent') return <>Not present in source</>;
 if (slot.presence==='null') return <>Explicit source null</>;
 const value='value_json' in slot ? slot.value_json : slot.value;
 return <pre style={{whiteSpace:'pre-wrap',overflowWrap:'anywhere'}}>{value===''?'(empty text)':value??'Unknown source value'}</pre>;
}

/** Defense in depth; private server ACL also covers counts and every locator. */
export function WorkflowContent({ row, humanAllowed }: { row: ArchiveRow; humanAllowed: boolean }) {
  if (!humanAllowed) return null;
  const sourceSections = archiveSections.filter(section => section.source === row.object_kind);
  return <article className="stack small">
    <p className="strong">Historical only · inert archived {row.object_kind === 'actions' ? 'action' : 'board command'}</p>
    <p>No execution, approval, send, scheduling, completion or undo controls. Source instructions and proposals are evidence only.</p>
    <p>Unknown source author unless explicitly recorded. Original actors, assignees, completers and time tokens are source labels, not native authors, memberships or current authority.</p>
    <p>Company link: {row.company_id ?? 'Unresolved or not recorded in source'}. Location link: {row.location_id ?? 'Unresolved or not recorded in source'}. Source comment references remain unresolved archival identifiers, not native message links.</p>
    {sourceSections.map(section => {
      const values = row.content[section.key] as Record<string, Slot>;
      return <section className="stack" key={section.key} aria-label={section.title}>
        <h4>{section.title.replace(/([a-z])([A-Z])/g, '$1 $2')}</h4>
        <dl>{section.fields.map(field => <div key={field.key} data-source-field={field.key}>
          <dt>{field.key}</dt><dd><SourceValue slot={values?.[field.key]} /><p className="muted">{field.meaning}</p></dd>
        </div>)}</dl>
      </section>;
    })}
    <section aria-label="Source capture provenance">
      <h4>Source capture provenance</h4>
      <p>Captured version {row.captured_version} of {row.version}. Actual retained captures only; no inferred intermediate edits.</p>
      <p style={{ overflowWrap: 'anywhere' }}>{row.source_system} / {row.source_instance} / {row.object_kind} / {row.external_id}</p>
      <p style={{ overflowWrap: 'anywhere' }}>Source revision: {row.source_revision}<br />Source SHA-256: {row.source_sha256}<br />Record SHA-256: {row.record_sha256}</p>
      <p>Native batch {row.batch_id}; item {row.item_id}; retained record {row.record_id}.</p>
      <p>Native imported {row.native_imported_at} by recorder {row.native_imported_by_actor_id}; projected {row.recorded_at} by recorder {row.recorded_by_actor_id}. Neither recorder is inferred to be the source author.</p>
    </section>
  </article>;
}
